export function PlanetDots({
  count,
  color,
  scale,
  hubRadius,
  dotRadius = 3.5,
  displaySize,
  ring = false,
}: {
  count: number;
  color: string;
  scale: number;
  hubRadius: number;
  dotRadius?: number;
  displaySize?: number;
  /** If true, distribute dots evenly around a single circle at `hubRadius`.
   *  Used for the final ALPHA ZONE step so the 10 dots read as a clean
   *  halo rather than a Fibonacci spiral. */
  ring?: boolean;
}) {
  const GOLDEN = Math.PI * (3 - Math.sqrt(5));
  // Round to 2 decimals so SSR + CSR emit byte-identical SVG attributes.
  const round = (n: number) => Math.round(n * 100) / 100;
  const dots: { x: number; y: number }[] = [];
  if (ring) {
    // Evenly spaced around one circle. Start angle -π/2 so the first dot is
    // at the top; visually balanced for any count.
    for (let i = 0; i < count; i++) {
      const a = -Math.PI / 2 + (2 * Math.PI * i) / count;
      dots.push({ x: round(hubRadius * Math.cos(a)), y: round(hubRadius * Math.sin(a)) });
    }
  } else {
    for (let i = 0; i < count; i++) {
      const r = hubRadius + scale * Math.sqrt(i);
      const a = i * GOLDEN;
      dots.push({ x: round(r * Math.cos(a)), y: round(r * Math.sin(a)) });
    }
  }
  const maxR = ring ? hubRadius : hubRadius + scale * Math.sqrt(count - 1);
  const padding = dotRadius + 4;
  const size = round((maxR + padding) * 2);
  const renderSize = displaySize ?? size;
  return (
    <svg
      viewBox={`${-size / 2} ${-size / 2} ${size} ${size}`}
      width={renderSize}
      height={renderSize}
      className="block"
      style={displaySize ? undefined : { maxWidth: '100%', maxHeight: '100%' }}
      aria-hidden
    >
      {dots.map((d, i) => (
        <circle key={i} cx={d.x} cy={d.y} r={dotRadius} fill={color} opacity={0.9} />
      ))}
    </svg>
  );
}
